import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Play, Pause, Volume2, VolumeX, Maximize, Settings, FileText, Code, Download, Copy, CheckCircle, BookOpen, MessageSquare, Share } from 'lucide-react';
import Button from '../../../components/ui/Button';

const ContentArea = ({ 
  tutorial, 
  lesson, 
  notes, 
  setNotes, 
  currentUser 
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [currentNote, setCurrentNote] = useState('');

  const handleAddNote = () => {
    if (!currentUser || !currentNote?.trim()) return;
    
    const newNote = {
      id: Date.now(),
      content: currentNote,
      timestamp: new Date()?.toISOString(),
      lessonId: lesson?.id
    };
    
    const tutorialNotes = notes?.[tutorial?.id] || [];
    const updatedNotes = {
      ...notes,
      [tutorial?.id]: [...tutorialNotes, newNote]
    };
    
    setNotes(updatedNotes);
    localStorage.setItem(`notes_${currentUser?.id}`, JSON.stringify(updatedNotes));
    setCurrentNote('');
  };

  const renderVideoPlayer = () => (
    <div className="relative bg-black rounded-lg overflow-hidden mb-6">
      <div className="aspect-video bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
        <div className="text-center text-white">
          <Play className="w-16 h-16 mx-auto mb-4 opacity-70" />
          <p className="text-lg mb-2">{lesson?.title}</p>
          <p className="text-sm opacity-70">Video content would load here</p>
        </div>
      </div>
      
      {/* Video Controls */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setIsPlaying(!isPlaying)}
            className="text-white hover:text-blue-400 transition-colors"
          >
            {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
          </button>
          
          <div className="flex-1 bg-white/20 rounded-full h-2">
            <div className="bg-blue-500 h-2 rounded-full w-1/3" />
          </div>
          
          <button
            onClick={() => setIsMuted(!isMuted)}
            className="text-white hover:text-blue-400 transition-colors"
          >
            {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
          </button>
          
          <button className="text-white hover:text-blue-400 transition-colors">
            <Settings className="w-5 h-5" />
          </button>
          
          <button className="text-white hover:text-blue-400 transition-colors">
            <Maximize className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );

  const renderInteractiveContent = () => (
    <div className="bg-card border border-border rounded-lg p-6 mb-6">
      <div className="flex items-center space-x-3 mb-4">
        <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
          <Play className="w-6 h-6 text-white" />
        </div>
        <div>
          <h3 className="font-semibold">{lesson?.title}</h3>
          <p className="text-sm text-muted-foreground">Interactive Exercise</p>
        </div>
      </div>
      
      <div className="bg-muted/20 rounded-lg p-4 mb-4">
        <p className="text-sm mb-3">
          This interactive lesson will guide you through hands-on practice. 
          Complete the exercises below to master the concepts.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-background border border-border rounded-lg p-4">
            <h4 className="font-medium mb-2">Exercise 1</h4>
            <p className="text-sm text-muted-foreground mb-3">
              Practice the concepts you've learned
            </p>
            <Button size="sm" variant="outline">Start Exercise</Button>
          </div>
          
          <div className="bg-background border border-border rounded-lg p-4">
            <h4 className="font-medium mb-2">Exercise 2</h4>
            <p className="text-sm text-muted-foreground mb-3">
              Apply your knowledge to real scenarios
            </p>
            <Button size="sm" variant="outline">Start Exercise</Button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTextContent = () => (
    <div className="prose prose-lg dark:prose-invert max-w-none mb-6">
      <h2>{lesson?.title}</h2>
      
      <p className="lead">
        Welcome to this comprehensive lesson on {lesson?.title}. In this section, 
        you'll learn the fundamental concepts and practical applications.
      </p>
      
      <h3>Learning Objectives</h3>
      <ul>
        <li>Understand the core concepts</li>
        <li>Learn practical implementation techniques</li>
        <li>Practice with real-world examples</li>
        <li>Master best practices and common patterns</li>
      </ul>
      
      <h3>Core Concepts</h3>
      <p>
        This lesson covers the essential knowledge you need to understand before 
        moving on to more advanced topics. We'll start with the basics and gradually 
        build up your understanding through examples and exercises.
      </p>
      
      <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-4 my-6">
        <p className="text-blue-800 dark:text-blue-200 mb-0">
          <strong>Pro Tip:</strong> Take notes as you go through this lesson. 
          The concepts covered here will be referenced in later lessons.
        </p>
      </div>
      
      <h3>Practical Examples</h3>
      <p>
        Let's look at some practical examples to solidify your understanding:
      </p>
    </div>
  );

  const renderCodeContent = () => (
    <div className="mb-6">
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="bg-muted px-4 py-2 border-b border-border flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Code className="w-4 h-4" />
            <span className="text-sm font-medium">Code Example</span>
          </div>
          <Button size="sm" variant="outline" className="flex items-center space-x-2">
            <Copy className="w-3 h-3" />
            <span>Copy</span>
          </Button>
        </div>
        
        <div className="p-4 bg-gray-900 text-gray-100 overflow-x-auto">
          <pre className="text-sm">
            <code>{`# Example: Basic Test Case Structure
import unittest
from selenium import webdriver
from selenium.webdriver.common.by import By

class TestExample(unittest.TestCase):
    def setUp(self):
        self.driver = webdriver.Chrome()
        self.driver.get("https://example.com")
    
    def test_login_functionality(self):
        # Find username field and enter text
        username = self.driver.find_element(By.ID, "username")
        username.send_keys("testuser")
        
        # Find password field and enter text
        password = self.driver.find_element(By.ID, "password")
        password.send_keys("testpass")
        
        # Click login button
        login_btn = self.driver.find_element(By.ID, "login")
        login_btn.click()
        
        # Verify successful login
        self.assertIn("Dashboard", self.driver.title)
    
    def tearDown(self):
        self.driver.quit()

if __name__ == "__main__":
    unittest.main()`}</code>
          </pre>
        </div>
      </div>
      
      <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
        <h4 className="font-medium text-green-800 dark:text-green-200 mb-2">
          Code Explanation
        </h4>
        <p className="text-sm text-green-700 dark:text-green-300">
          This example demonstrates a basic test case structure using Python and Selenium. 
          Notice how we set up the WebDriver, perform actions, make assertions, and clean up resources.
        </p>
      </div>
    </div>
  );

  const renderDownloadContent = () => (
    <div className="bg-card border border-border rounded-lg p-6 mb-6">
      <div className="flex items-center space-x-3 mb-4">
        <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
          <Download className="w-6 h-6 text-white" />
        </div>
        <div>
          <h3 className="font-semibold">{lesson?.title}</h3>
          <p className="text-sm text-muted-foreground">Downloadable Resources</p>
        </div>
      </div>
      
      <p className="text-muted-foreground mb-4">
        This lesson includes downloadable resources to help you practice and reference the material.
      </p>
      
      <div className="space-y-3">
        <div className="flex items-center justify-between p-3 border border-border rounded-lg">
          <div className="flex items-center space-x-3">
            <FileText className="w-5 h-5 text-muted-foreground" />
            <div>
              <div className="font-medium">Test Case Template</div>
              <div className="text-xs text-muted-foreground">PDF • 245 KB</div>
            </div>
          </div>
          <Button size="sm" variant="outline">Download</Button>
        </div>
        
        <div className="flex items-center justify-between p-3 border border-border rounded-lg">
          <div className="flex items-center space-x-3">
            <Code className="w-5 h-5 text-muted-foreground" />
            <div>
              <div className="font-medium">Code Examples</div>
              <div className="text-xs text-muted-foreground">ZIP • 1.2 MB</div>
            </div>
          </div>
          <Button size="sm" variant="outline">Download</Button>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (lesson?.type) {
      case 'video':
        return renderVideoPlayer();
      case 'interactive':
        return renderInteractiveContent();
      case 'code':
        return renderCodeContent();
      case 'download':
        return renderDownloadContent();
      default:
        return renderTextContent();
    }
  };

  if (!lesson) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Select a Lesson</h3>
          <p className="text-muted-foreground">
            Choose a lesson from the sidebar to begin learning.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto">
      <div className="max-w-4xl mx-auto p-6">
        {/* Lesson Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                {lesson?.type === 'video' && <Play className="w-5 h-5 text-white" />}
                {lesson?.type === 'interactive' && <CheckCircle className="w-5 h-5 text-white" />}
                {lesson?.type === 'text' && <FileText className="w-5 h-5 text-white" />}
                {lesson?.type === 'code' && <Code className="w-5 h-5 text-white" />}
                {lesson?.type === 'download' && <Download className="w-5 h-5 text-white" />}
              </div>
              <div>
                <h1 className="text-2xl font-bold">{lesson?.title}</h1>
                <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                  <span>Duration: {lesson?.duration}</span>
                  <span className="capitalize">{lesson?.type} Lesson</span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowNotes(!showNotes)}
                className="flex items-center space-x-2"
              >
                <MessageSquare className="w-4 h-4" />
                <span>Notes</span>
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                className="flex items-center space-x-2"
              >
                <Share className="w-4 h-4" />
                <span>Share</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        {renderContent()}

        {/* Notes Section */}
        {showNotes && currentUser && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-card border border-border rounded-lg p-4 mb-6"
          >
            <h3 className="font-semibold mb-3">Your Notes</h3>
            
            <div className="space-y-3 mb-4">
              {notes?.[tutorial?.id]?.filter(note => note?.lessonId === lesson?.id)?.map(note => (
                <div key={note?.id} className="bg-muted/50 p-3 rounded-lg">
                  <p className="text-sm">{note?.content}</p>
                  <div className="text-xs text-muted-foreground mt-1">
                    {new Date(note?.timestamp)?.toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="flex space-x-2">
              <input
                type="text"
                value={currentNote}
                onChange={(e) => setCurrentNote(e?.target?.value)}
                placeholder="Add a note about this lesson..."
                className="flex-1 px-3 py-2 border border-border rounded-lg bg-background text-sm"
                onKeyPress={(e) => e?.key === 'Enter' && handleAddNote()}
              />
              <Button onClick={handleAddNote} size="sm">
                Add Note
              </Button>
            </div>
          </motion.div>
        )}

        {/* Lesson Navigation */}
        <div className="flex items-center justify-between pt-6 border-t border-border">
          <Button variant="outline" className="flex items-center space-x-2">
            <span>← Previous Lesson</span>
          </Button>
          
          <div className="text-sm text-muted-foreground">
            Lesson {lesson?.id} • {tutorial?.title}
          </div>
          
          <Button className="flex items-center space-x-2">
            <span>Next Lesson →</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ContentArea;